import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Stethoscope, Warning, CheckCircle, Wrench, ArrowClockwise } from "@phosphor-icons/react";

/**
 * Admin health check for the MongoDB collections. Scans for legacy / malformed
 * documents (missing critical fields) and offers one-click auto-fix where safe.
 * Backing endpoint: `/api/admin/db-healthcheck` + `/api/admin/db-healthcheck/fix/{key}`.
 */
export default function DbHealthPanel() {
  const [report, setReport] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [fixing, setFixing] = useState(null);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await api.get("/admin/db-healthcheck");
      setReport(r.data);
      const total = r.data?.total_issues || 0;
      if (total === 0) toast.success("Base saine · aucun problème détecté");
      else toast.warning(`${total} document${total > 1 ? "s" : ""} à surveiller`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setScanning(false); }
  };

  const applyFix = async (key) => {
    if (!window.confirm("Appliquer la correction automatique ? L'opération est idempotente.")) return;
    setFixing(key);
    try {
      const r = await api.post(`/admin/db-healthcheck/fix/${key}`);
      const n = r.data?.result?.fixed ?? 0;
      toast.success(n > 0 ? `${n} documents corrigés` : "Rien à corriger (déjà propre)");
      await scan();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setFixing(null); }
  };

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-4" data-testid="db-health-panel">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
          <Stethoscope size={20} weight="fill" className="text-emerald-400" />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-sm">Check-up santé de la base</div>
          <div className="text-[11px] text-white/60 mt-0.5">
            Scanne les collections MongoDB pour repérer les documents legacy sans champs critiques
            (comme le bug récent des congés sans <code className="text-white/80">days</code>).
            Corrige en un clic ce qui est corrigible.
          </div>
        </div>
        <Button
          onClick={scan}
          disabled={scanning}
          className="bg-[#005CFF] hover:bg-[#0047CC] gap-2 h-9"
          data-testid="db-health-scan"
        >
          {scanning ? (
            <><div className="w-3.5 h-3.5 border border-white/40 border-t-white rounded-full animate-spin" /> Analyse…</>
          ) : (
            <><ArrowClockwise size={13} weight="bold" /> Lancer le check-up</>
          )}
        </Button>
      </div>

      {report && (
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-widest text-white/50 flex items-center justify-between">
            <span>Rapport du {new Date(report.checked_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
            <span className={report.total_issues === 0 ? "text-emerald-400" : "text-amber-400"}>
              {report.total_issues} anomalie{report.total_issues !== 1 ? "s" : ""}
            </span>
          </div>
          {report.checks.map((c) => {
            const ok = c.count === 0;
            return (
              <div
                key={c.key}
                className={`rounded-lg p-3 border flex items-start gap-3 ${
                  ok
                    ? "bg-emerald-500/[0.06] border-emerald-500/20"
                    : c.severity === "critical"
                    ? "bg-red-500/[0.08] border-red-500/30"
                    : "bg-amber-500/[0.08] border-amber-500/25"
                }`}
                data-testid={`db-health-check-${c.key}`}
              >
                <div className="shrink-0 pt-0.5">
                  {ok ? <CheckCircle size={16} weight="fill" className="text-emerald-400" /> : <Warning size={16} weight="fill" className={c.severity === "critical" ? "text-red-400" : "text-amber-400"} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium flex items-center gap-2">
                    <span>{c.label}</span>
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${ok ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-white/70"}`}>
                      {c.count}
                    </span>
                    <span className="text-[9px] text-white/40 font-mono">{c.collection}</span>
                  </div>
                  {!ok && (
                    <>
                      <div className="text-[11px] text-white/60 mt-0.5">{c.hint}</div>
                      {c.samples.length > 0 && (
                        <div className="text-[10px] text-white/40 mt-1 font-mono">
                          Exemples: {c.samples.map((s) => s.id || "?").join(", ")}
                        </div>
                      )}
                    </>
                  )}
                </div>
                {!ok && c.fixable && (
                  <Button
                    size="sm"
                    onClick={() => applyFix(c.key)}
                    disabled={!!fixing}
                    className="shrink-0 h-8 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold gap-1.5"
                    data-testid={`db-health-fix-${c.key}`}
                  >
                    {fixing === c.key ? (
                      <><div className="w-3 h-3 border border-black/40 border-t-black rounded-full animate-spin" /> …</>
                    ) : (
                      <><Wrench size={11} weight="bold" /> Corriger</>
                    )}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!report && !scanning && (
        <div className="text-center py-6 text-[11px] text-white/40">
          Clique sur <b>Lancer le check-up</b> pour analyser l&apos;état de la base.
        </div>
      )}
    </div>
  );
}
